// Live tie-out of the headline outputs: every figure re-derived from first principles and compared to the model.
import * as m from "./model.js";

const npv = (flows, r) => flows.reduce((a, cf, i) => a + cf / Math.pow(1 + r / 100, i), 0);
const pmt = (P, rate, yrs) => { if (P <= 0 || yrs <= 0) return 0; const r = rate / 100 / 12, n = yrs * 12; return r ? (P * r / (1 - Math.pow(1 + r, -n))) * 12 : P / yrs; };

export function auditOutputs(V) {
  const fails = [];
  let checks = 0;
  const ok = (label, a, b, tol = 1) => { checks++; if (!(Math.abs(a - b) <= tol)) fails.push({ label, model: a, hand: b }); };
  if (!V || !(V.sde > 0)) return { checks: 0, fails, clean: true, skipped: true };

  const su = m.sourcesUses(V);
  ok("Sources & Uses balance", su.totalUses, su.totalSources);
  ok("Uses lines sum to Total Uses", su.uses.reduce((a, u) => a + u[1], 0), su.totalUses);
  ok("Sources lines sum to Total Sources", su.sources.reduce((a, u) => a + u[1], 0), su.totalSources);

  const senior = m.loanAmt(V), ds = pmt(senior, V.rate, V.loanYrs) + pmt(V.snAmt || 0, V.snRate, V.snYrs);
  ok("Annual debt service (amortisation)", m.totalAnnualDS(V), ds, 0.01);
  const cf = V.sde - V.salary - V.capex - (V.slbOn ? V.sqft * V.rentRate : 0) - m.mgmtFee(V);
  ok("CFADS Yr 1 = SDE − salary − capex − rent − mgmt fee", m.cfads(V), cf, 0.01);
  ok("DSCR Yr 1 = CFADS ÷ debt service", m.dscrYr1(V), ds > 0 ? cf / ds : 0, 1e-6);
  const rows = m.model5yr(V);
  rows.forEach((r) => { ok(`Yr ${r.y} FCF = CFADS − debt service`, r.fcf, r.cfads - r.ds_y, 0.01); ok(`Yr ${r.y} DSCR`, r.dscr, r.ds_y > 0 ? r.cfads / r.ds_y : 0, 1e-6); });
  const H = m.effectiveHold(V);
  ok("Exit-year platform SDE growth", rows[H - 1].plat_y, V.sde * Math.pow(1 + V.sdeGrowth / 100, H - 1), 0.01);

  const ex = m.exitReturns(V);
  ok("Exit EV = exit-year SDE × exit multiple", ex.exitEV, rows[H - 1].sde_y * V.exitMult, 0.01);
  ok("Exit equity = EV − debt − seller rollover common share + cash reserve", ex.equityAtExit,
    Math.max(0, (V.holdIndefinite ? 0 : Math.max(0, ex.exitEV - ex.loanBal)) - (ex.rollExit || 0)) + ex.leftoverReserve, 0.01);
  ok("Seller rollover share = pool after LP liquidation preference and promote, capped at ownership % of exit equity", ex.rollExit || 0,
    Math.min(ex.rollResidual || 0, (ex.baseEquityAtExit || 0) * ((ex.rollOwnPct || 0) / 100)), 0.01);
  ok("Seller MOIC on rolled equity = exit proceeds ÷ rolled equity", ex.rollMoic || 0,
    (ex.rollEquity || 0) > 0 ? (ex.rollExit || 0) / ex.rollEquity : 0, 0.0001);
  ok("Seller rollover share never exceeds its ownership % of exit equity", 1,
    (ex.rollExit || 0) <= (ex.baseEquityAtExit || 0) * ((ex.rollOwnPct || 0) / 100) + 0.01 ? 1 : 0, 0);
  if ((ex.rollOwnPct || 0) > 0.01) {
    ok("LP liquidation preference ≥ multiple × LP invested capital", 1,
      (ex.liqPref || 0) >= (ex.liqPrefX || 0) * m.lpWaterfall(V).lpEquity - 1 ? 1 : 0, 0);
    ok("Liquidation preference still due ≤ total preference (interim cash counts)", 1,
      (ex.liqPrefDue || 0) <= (ex.liqPref || 0) + 0.01 ? 1 : 0, 0);
  }
  if (!m.backLever(V)?.active) ok("Senior balance at exit", ex.seniorBal, m.remainingBalance(senior, V.rate, V.loanYrs, H * 12), 0.01);
  const plan = m.cashPlan(V, rows);
  const flows = [-ex.equityInvested0, ...plan.rows.map((r, i) => r.distributable - r.called + (i === plan.rows.length - 1 ? ex.equityAtExit : 0))];
  ok("Equity MOIC = (distributions + exit) ÷ invested", ex.moic, (plan.rows.reduce((a, r) => a + r.distributable, 0) + ex.equityAtExit) / ex.equityInvested, 1e-6);
  if (!isNaN(ex.irr)) ok("Equity IRR discounts flows to zero", npv(flows, ex.irr), 0, 5);
  if (m.isRollupScope(V)) {
    ok("Equity invested = Day-0 equity + capital calls", ex.equityInvested, ex.equityInvested0 + plan.totalCalled, 1);
    ok("S&U capital calls = cash plan calls", su.called, plan.totalCalled, 1);
    ok("Day-0 equity includes add-on equity funded at close", ex.equityInvested0, ex.equityAtCloseBase + plan.fundedAtClose, 1);
    ok("S&U LP equity = net LP raise + GP co-invest", su.equity, ex.lpAsk + ex.gpShare, 1);
    ok("LP add-on equity (close + calls) = committed add-on LP need", su.lpAddonEquity, plan.rows.reduce((a, r) => a + r.lpNeed, 0), 1);
  } else if (m.isRecap(V)) ok("Equity invested = new LP + rolled equity", ex.equityInvested0, su.recap.newLp + su.recap.rolled, 1);
  else ok("Equity invested = S&U sponsor equity", ex.equityInvested0, su.equity, 1);

  const wf = m.lpWaterfall(V);
  const cashIn = wf.rows.reduce((a, r) => a + r.cashAvail, 0);
  ok("Waterfall: LP + GP = cash distributed", wf.lpTotal + wf.gpTotal, cashIn, 1);
  ok("Waterfall: pref + capital + promote split = LP total", wf.prefTotal + wf.capTotal + wf.rows.reduce((a, r) => a + r.splitLp, 0), wf.lpTotal, 1);
  ok("LP MOIC = LP total ÷ LP equity", wf.lpMoic, wf.lpTotal / wf.lpEquity, 1e-9);
  const lpFlows = [-wf.lpEquity0, ...wf.rows.map((r) => r.lpYear - r.called)];
  if (!isNaN(wf.lpIrr)) ok("LP IRR discounts LP flows to zero", npv(lpFlows, wf.lpIrr), 0, 5);
  const inv = m.investorReturn(V, 100000);
  if (!isNaN(inv.irr) && !isNaN(wf.lpIrr)) ok("Investor calculator IRR = LP IRR", inv.irr, wf.lpIrr, 0.01);
  ok("Investor calculator MOIC = LP MOIC", inv.moic, wf.lpMoic, 1e-6);

  if (m.isRollupScope(V)) {
    const ro = m.rollupModelFor(V);
    ok("Blended entry = total price ÷ total EBITDA", ro.blendedEntry, ro.totPrice / ro.totEbitda, 1e-9);
    ok("Entry multiple shown = blended entry", m.entryMultiple(V), ro.blendedEntry, 1e-9);
    ok("Pro-forma SDE = platform + add-ons + self-funded", m.proForma(V).sde, ro.totEbitda + (m.sfGroupRow(V)?.ebitda || 0), 0.01);
    const sfRow = m.sfGroupRow(V);
    if (sfRow) {
      ok("Self-funded add-ons: equity = price − debt − note − rollover + WC + fees", sfRow.equity, sfRow.price - sfRow.debt - sfRow.sellerNote - sfRow.rollover + sfRow.nwc + sfRow.fees, 0.01);
      ok("Self-funded add-ons never enter the LP equity ask", ex.equityInvested, ex.equityInvested0 + plan.totalCalled, 1);
      ok("Cash-only add-ons (self-funded + back-lever) fully paid from the reserve", plan.totalSelfFunded, m.consolidatedAddons(V).filter((c) => c.selfFunded || c.cashFunded).reduce((a, c) => a + (c.fromCF || 0), 0), 1);
    }
    ro.rows.forEach((r) => ok(`${r.name}: equity = price − debt − note − rollover + WC + fees`, r.equity, r.price - r.debt - r.sellerNote - r.rollover + r.nwc + r.fees, 0.01));
    const cov = m.coverage(V);
    ok("Headline DSCR = fully-deployed year row", cov.dscr, rows[cov.year - 1].dscr, 1e-9);
  } else ok("Entry multiple = NAC ÷ SDE", m.entryMultiple(V), m.nac(V) / V.sde, 1e-9);

  if (m.isRecap(V)) {
    const r = su.recap || m.recapModel(V), L = m.recapLegacy(V), R = m.recapCfg(V);
    ok("Recap EV = SDE × recap multiple", L.ev, V.sde * (Number(R.recapMult) || 0), 0.01);
    ok("Legacy cash-out = (EV − existing debt) × cash-out %", L.cashOut, Math.max(0, L.ev - (Number(R.existingDebt) || 0)) * (Number(R.cashOutPct) || 0) / 100, 0.01);
    ok("New LP equity = uses − new debt − rolled − note − SLB", r.newLp,
       Math.max(0, r.totalUses - r.newDebt - L.rolled - (V.snAmt || 0) - (r.slb || 0)), 0.01);
  }

  // ---- Projected P&L tie-out: every displayed figure is derived, every column reconciles ----
  const pl = m.projectedPL(V);
  const gmV = m.grossMargin(V);
  pl.forEach((r) => {
    ok(`PL Yr ${r.y}: Revenue − COGS = Gross profit`, r.revenue - r.cogs, r.gp, 1);
    ok(`PL Yr ${r.y}: Gross profit − Opex = EBITDA`, r.gp - r.opex, r.ebitda, 1);
    ok(`PL Yr ${r.y}: EBITDA margin = EBITDA ÷ Revenue`, r.ebitdaPct, r.revenue ? (r.ebitda / r.revenue) * 100 : 0, 0.1);
    ok(`PL Yr ${r.y}: Gross margin = Gross profit ÷ Revenue`, r.gmPct, r.revenue ? (r.gp / r.revenue) * 100 : 0, 0.1);
    ok(`PL Yr ${r.y}: EBITDA − owner comp − capex − rent − mgmt = CFADS`, r.ebitda - r.salary - r.capex - r.rent - r.mgmt, r.cfads, 1);
    ok(`PL Yr ${r.y}: CFADS − debt service = FCF`, r.cfads - r.ds, r.fcf, 1);
    ok(`PL Yr ${r.y}: EBITDA > 0 cannot coexist with Opex > Gross profit`, 1, (r.ebitda > 0 && r.opex > r.gp) ? 0 : 1, 0);
  });
  // Debt service must be present at close, and the back-lever facility must be serviced after it draws.
  if (m.loanAmt(V) > 0 || (V.snAmt || 0) > 0) {
    ok("PL Yr 1: debt service > 0 while debt is outstanding", 1, pl[0] && pl[0].ds > 0 ? 1 : 0, 0);
  }
  const blAudit = m.backLever(V);
  if (blAudit?.active) {
    const refiRow = pl.find((r) => r.y > blAudit.year);
    if (refiRow) ok(`PL Yr ${refiRow.y}: back-lever facility is serviced after it draws`, 1, refiRow.ds > 0 ? 1 : 0, 0);
  }
  // Pro-forma (all add-ons closed) column reconciles the same way.
  const pf = m.proForma(V);
  if (pf.pf) {
    const pfGp = pf.revenue * (gmV / 100), pfCogs = pf.revenue - pfGp, pfOpex = pfGp - pf.sde;
    ok("PL Pro-forma: Revenue − COGS = Gross profit", pf.revenue - pfCogs, pfGp, 1);
    ok("PL Pro-forma: Gross profit − Opex = EBITDA", pfGp - pfOpex, pf.sde, 1);
  }

  return { checks, fails, clean: fails.length === 0, skipped: false };
}
